/**
 * Settings › Remote access, stage 1 (docs/PLAN-REMOTE-ACCESS-2026-09-25.md).
 *
 * The switches, the hosts, and every session there has been. Two kinds:
 * `diagnostic` (stage 1) — the host says who it is and echoes — and `shell`
 * (stage 2), root or SYSTEM, recorded in full, behind a second switch per host.
 *
 * The screen decides nothing. Every control here is enforced by the API and
 * again by the relay; this only shows the state and asks for what they need.
 */

import { Download, Monitor, Network, Play, Plug, Power, Square, TerminalSquare } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError, api } from './api.ts';
import { Banner, Button, DataTable, Drawer, Empty, Field, Loading, Pill, Section, Toolbar, type ColumnDef } from './components/index.ts';
import { DesktopWindow, openDesktop, type DesktopConnection } from './DesktopViewer.tsx';
import { RecordingPlayer } from './RecordingPlayer.tsx';
import { ShellTerminal, openShellSocket, type OpenedSession, type ShellSocket } from './ShellTerminal.tsx';

/** How a session's second factor was given (066 adds `recent`: a code from the last hour). */
const MFA_VIA_WORDS: Record<string, string> = {
  totp: 'authenticator code',
  recovery: 'recovery code',
  recent: 'code from the last hour',
};

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

interface RemoteHost {
  assetId: string;
  label: string;
  agentId: string;
  agentVersion: string | null;
  os: string | null;
  lastReportAt: string | null;
  enabled: boolean;
  shellEnabled: boolean;
  desktopEnabled: boolean;
  wireguard: {
    enabled: boolean;
    address: string | null;
    appliedAt: string | null;
    error: string | null;
    lastHandshakeAt: string | null;
    hostNote: string | null;
  };
}

/** A tunnel's state in a few words, from what the hub and the host last said. */
function tunnelState(w: RemoteHost['wireguard']): { text: string; tone: 'ok' | 'warning' | 'neutral' | 'fault' } {
  if (!w.enabled) return { text: w.address === null ? '' : `was ${w.address}`, tone: 'neutral' };
  if (w.hostNote !== null) return { text: w.hostNote, tone: 'warning' };
  if (w.error !== null) return { text: w.error, tone: 'fault' };
  if (w.address === null) return { text: 'waiting for the host to register', tone: 'warning' };
  if (w.appliedAt === null) return { text: `${w.address} · waiting for the hub`, tone: 'warning' };
  if (w.lastHandshakeAt === null) return { text: `${w.address} · no handshake yet`, tone: 'warning' };
  const mins = Math.floor((Date.now() - new Date(w.lastHandshakeAt).getTime()) / 60_000);
  // WireGuard re-handshakes every two minutes while traffic flows, and the keepalive keeps it flowing.
  return mins <= 3
    ? { text: `${w.address} · up`, tone: 'ok' }
    : { text: `${w.address} · last handshake ${mins < 90 ? `${mins}m` : `${Math.floor(mins / 60)}h`} ago`, tone: 'warning' };
}

interface RemoteSession {
  id: string;
  assetId: string;
  label: string;
  kind: string;
  openedByLabel: string;
  reason: string;
  mfaVia: string;
  createdAt: string;
  browserJoinedAt: string | null;
  hostJoinedAt: string | null;
  lastActivityAt: string | null;
  bytesToHost: number;
  bytesFromHost: number;
  endedAt: string | null;
  endReason: string | null;
  recordedBytes: number;
}

interface RemoteAccess {
  tenantEnabled: boolean;
  platformHalted: boolean;
  canOpen: boolean;
  /** Why not, when canOpen is false (069). */
  cannotOpen?: string;
  /** Platform staff: the platform-wide halt is theirs. */
  platformStaff?: boolean;
  kinds: string[];
  hosts: RemoteHost[];
  sessions: RemoteSession[];
}

const END_TEXT: Record<string, string> = {
  closed_by_user: 'closed by the person who opened it',
  ended_by_staff: 'ended by an administrator',
  browser_left: 'browser disconnected',
  host_left: 'host disconnected or declined',
  idle: 'idle for 15 minutes',
  time_limit: 'reached the 4-hour limit',
  host_switch_off: 'host switch turned off',
  tenant_switch_off: 'organisation switch turned off',
  platform_halt: 'halted platform-wide',
  agent_revoked: 'agent revoked',
  agent_offline: 'host was not connected',
  never_joined: 'never connected',
  relay_restart: 'relay restarted',
  shell_switch_off: 'shell switch turned off',
  shell_exited: 'the shell exited',
  desktop_switch_off: 'desktop switch turned off',
  access_revoked: 'remote access withdrawn from the person',
  recording_failed: 'recording could not be written — ended',
  recording_limit: 'recording reached its size limit — ended',
};

const reporting = (h: RemoteHost): boolean =>
  h.lastReportAt !== null && Date.now() - new Date(h.lastReportAt).getTime() < 5 * 60_000;

function duration(s: RemoteSession): string {
  const start = new Date(s.createdAt).getTime();
  const end = s.endedAt === null ? Date.now() : new Date(s.endedAt).getTime();
  const secs = Math.max(0, Math.round((end - start) / 1000));
  return secs < 90 ? `${secs}s` : `${Math.round(secs / 60)}m`;
}

export function RemoteAccessView(): React.JSX.Element {
  const [data, setData] = useState<RemoteAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState<RemoteHost | null>(null);
  const [playing, setPlaying] = useState<RemoteSession | null>(null);
  const [desktop, setDesktop] = useState<{ connection: DesktopConnection; label: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<RemoteAccess>('/api/remote-access'));
      setError(null);
    } catch (err) {
      setError(failure(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn();
        await load();
      } catch (err) {
        setError(failure(err));
      }
    },
    [load],
  );

  const hostColumns = useMemo<ColumnDef<RemoteHost, unknown>[]>(
    () => [
      {
        id: 'host',
        header: 'Host',
        accessorFn: (h) => h.label.toLowerCase(),
        cell: ({ row }) => (
          <div>
            <div className="cell-strong">{row.original.label}</div>
            <div className="small muted">
              {row.original.os ?? '?'} · agent {row.original.agentVersion ?? '?'}
            </div>
          </div>
        ),
      },
      {
        id: 'reporting',
        header: 'Agent',
        accessorFn: (h) => (reporting(h) ? 'reporting' : 'quiet'),
        cell: ({ row }) =>
          reporting(row.original) ? (
            <Pill state="ok" reason="Reports are arriving.">reporting</Pill>
          ) : (
            <Pill state="warning" reason="Nothing for five minutes: a session would fail to reach it.">quiet</Pill>
          ),
        size: 120,
      },
      {
        id: 'enabled',
        header: 'Remote sessions',
        accessorFn: (h) => (h.enabled ? 'on' : 'off'),
        cell: ({ row }) => (
          <Button
            size="sm"
            variant={row.original.enabled ? 'secondary' : 'ghost'}
            icon={<Power size={14} />}
            aria-label={`${row.original.enabled ? 'Turn off' : 'Turn on'} remote sessions for ${row.original.label}`}
            onClick={() =>
              void act(() => api.put(`/api/agents/${row.original.agentId}/remote-access`, { enabled: !row.original.enabled }))
            }
          >
            {row.original.enabled ? 'On' : 'Off'}
          </Button>
        ),
        size: 150,
      },
      {
        id: 'shell',
        header: 'Shell (root)',
        accessorFn: (h) => (h.shellEnabled ? 'on' : 'off'),
        cell: ({ row }) => (
          <Button
            size="sm"
            variant={row.original.shellEnabled ? 'secondary' : 'ghost'}
            icon={<TerminalSquare size={14} />}
            aria-label={`${row.original.shellEnabled ? 'Turn off' : 'Turn on'} shell sessions for ${row.original.label}`}
            title="A root (Linux) or SYSTEM (Windows) shell, recorded in full. Separate from the session switch."
            onClick={() =>
              void act(() => api.put(`/api/agents/${row.original.agentId}/remote-shell`, { enabled: !row.original.shellEnabled }))
            }
          >
            {row.original.shellEnabled ? 'On' : 'Off'}
          </Button>
        ),
        size: 130,
      },
      {
        id: 'desktop',
        header: 'Desktop',
        accessorFn: (h) => (h.desktopEnabled ? 'on' : 'off'),
        cell: ({ row }) => (
          <Button
            size="sm"
            variant={row.original.desktopEnabled ? 'secondary' : 'ghost'}
            icon={<Monitor size={14} />}
            aria-label={`${row.original.desktopEnabled ? 'Turn off' : 'Turn on'} desktop sessions for ${row.original.label}`}
            title="The console user's screen, mouse and keyboard (Windows). The person at the PC sees a bar while anyone is connected."
            onClick={() =>
              void act(() => api.put(`/api/agents/${row.original.agentId}/remote-desktop`, { enabled: !row.original.desktopEnabled }))
            }
          >
            {row.original.desktopEnabled ? 'On' : 'Off'}
          </Button>
        ),
        size: 110,
      },
      {
        id: 'wireguard',
        header: 'VPN tunnel',
        accessorFn: (h) => (h.wireguard.enabled ? 'on' : 'off'),
        cell: ({ row }) => {
          const w = row.original.wireguard;
          const state = tunnelState(w);
          return (
            <div>
              <Button
                size="sm"
                variant={w.enabled ? 'secondary' : 'ghost'}
                icon={<Network size={14} />}
                aria-label={`${w.enabled ? 'Turn off' : 'Turn on'} the VPN tunnel for ${row.original.label}`}
                title="The host's own WireGuard tunnel to the hub (10.10.255.x), run by its OS so CosMon can reach it with the agent down. Routes only the admin block."
                onClick={() =>
                  void act(() => api.put(`/api/agents/${row.original.agentId}/wireguard`, { enabled: !w.enabled }))
                }
              >
                {w.enabled ? 'On' : 'Off'}
              </Button>
              {state.text !== '' && (
                <div className={state.tone === 'fault' ? 'small text-fault' : state.tone === 'neutral' ? 'small muted' : 'small'}>
                  {state.tone === 'ok' ? <Pill state="ok" reason="The hub heard from this host in the last few minutes.">{state.text}</Pill> : state.text}
                </div>
              )}
            </div>
          );
        },
        size: 220,
      },
      {
        id: 'open',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          data?.canOpen === true ? (
            <Button
              size="sm"
              variant="primary"
              icon={<Plug size={14} />}
              disabled={!row.original.enabled || data.platformHalted || !data.tenantEnabled}
              title={
                data.platformHalted
                  ? 'Remote access is halted platform-wide'
                  : !data.tenantEnabled
                    ? 'Turn remote access on for this organisation first'
                    : !row.original.enabled
                      ? 'Turn remote sessions on for this host first'
                      : 'Open a diagnostic session'
              }
              onClick={() => setOpening(row.original)}
            >
              Connect
            </Button>
          ) : null,
        size: 130,
      },
    ],
    [act, data],
  );

  const sessionColumns = useMemo<ColumnDef<RemoteSession, unknown>[]>(
    () => [
      {
        id: 'when',
        header: 'Opened',
        accessorFn: (s) => s.createdAt,
        cell: ({ row }) => <span className="small">{new Date(row.original.createdAt).toLocaleString()}</span>,
        size: 170,
      },
      {
        id: 'who',
        header: 'Who, where, why',
        accessorFn: (s) => `${s.openedByLabel} ${s.label}`,
        cell: ({ row }) => (
          <div>
            <div className="cell-strong">
              {row.original.openedByLabel} → {row.original.label}
            </div>
            <div className="small muted">
              {row.original.kind} · “{row.original.reason}” · MFA by {MFA_VIA_WORDS[row.original.mfaVia] ?? row.original.mfaVia}
            </div>
          </div>
        ),
      },
      {
        id: 'state',
        header: 'Outcome',
        accessorFn: (s) => s.endReason ?? 'open',
        cell: ({ row }) =>
          row.original.endedAt === null ? (
            <Pill state="warning" reason="Somebody is connected to this host now.">open</Pill>
          ) : (
            <span className="small">
              {duration(row.original)} · {END_TEXT[row.original.endReason ?? ''] ?? row.original.endReason}
            </span>
          ),
        size: 230,
      },
      {
        id: 'bytes',
        header: 'Traffic',
        accessorFn: (s) => s.bytesToHost + s.bytesFromHost,
        cell: ({ row }) => (
          <span className="small muted mono">
            ↑{row.original.bytesToHost} ↓{row.original.bytesFromHost}
          </span>
        ),
        size: 110,
      },
      {
        id: 'end',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.kind === 'shell' && row.original.endedAt !== null && row.original.recordedBytes > 0 ? (
            <div className="row-tight">
            <Button size="sm" variant="ghost" icon={<Play size={14} />} onClick={() => setPlaying(row.original)}>
              Play
            </Button>
            <a
              className="btn btn-ghost btn-sm"
              href={`/api/remote-sessions/${row.original.id}/recording`}
              download
              title="asciicast v2 — plays in asciinema. Opening it is audited."
            >
              <span className="btn-icon-slot">
                <Download size={14} />
              </span>
              <span>Recording</span>
            </a>
            </div>
          ) : row.original.endedAt === null ? (
            <Button
              size="sm"
              variant="danger"
              icon={<Square size={14} />}
              onClick={() => void act(() => api.post(`/api/remote-sessions/${row.original.id}/end`))}
            >
              End
            </Button>
          ) : null,
        size: 200,
      },
    ],
    [act],
  );

  if (error !== null && data === null) return <Banner tone="fault">{error}</Banner>;
  if (data === null) return <Loading rows={3} cols={4} label="Loading remote access" />;

  return (
    <>
      {error !== null && <Banner tone="fault">{error}</Banner>}
      {data.platformHalted && (
        <Banner tone="warn" title="Remote access is halted platform-wide">
          No session can be opened on any host until a platform administrator lifts the halt.
        </Banner>
      )}
      <Toolbar>
        <span className="small muted">
          Staff can connect to a host running the agent, with a reason and an MFA code (asked once an hour), both of which are kept.
          Every switch is off until someone turns it on, and turning any of them off ends the sessions it covers.
          A diagnostic session proves the path and touches nothing; a shell is root (or SYSTEM), has its own switch
          per host, and is recorded in full — the screen, never keystrokes.
        </span>
      </Toolbar>

      <Section
        title="This organisation"
        summary={data.tenantEnabled ? 'Remote sessions allowed' : 'Remote sessions not allowed'}
      >
        <div className="row">
          <Button
            variant={data.tenantEnabled ? 'secondary' : 'primary'}
            icon={<Power size={14} />}
            onClick={() => void act(() => api.put('/api/remote-access/tenant', { enabled: !data.tenantEnabled }))}
          >
            {data.tenantEnabled ? 'Stop allowing remote sessions' : 'Allow remote sessions'}
          </Button>
          <span className="small muted">Owners and administrators. Turning this off ends every open session here.</span>
        </div>
      </Section>

      {!data.canOpen && data.cannotOpen !== undefined && (
        // Where Connect would be, say why it is not (069): the switches alone
        // do not let anyone connect.
        <Banner tone="info" title="You cannot open sessions">
          {data.cannotOpen}.
        </Banner>
      )}
      <Section flush title="Hosts" summary={`${data.hosts.filter((h) => h.enabled).length} of ${data.hosts.length} accept sessions`}>
        <DataTable
          data={data.hosts}
          columns={hostColumns}
          getRowId={(h) => h.agentId}
          caption="Hosts that could accept a remote session"
          empty={<Empty title="No enrolled hosts" body="Enrol a host in Settings › Hosts first." />}
        />
      </Section>

      <Section flush title="Sessions" summary={`the last ${data.sessions.length}`}>
        <DataTable
          data={data.sessions}
          columns={sessionColumns}
          getRowId={(s) => s.id}
          initialSort={[{ id: 'when', desc: true }]}
          caption="Remote sessions, newest first"
          empty={<Empty title="No sessions yet" body="Every session opened on this organisation’s hosts is listed here." />}
        />
      </Section>

      {data.platformStaff === true && (
        <Section title="Platform" summary={data.platformHalted ? 'Halted' : 'Running'}>
          <div className="row">
            <Button
              variant={data.platformHalted ? 'primary' : 'danger'}
              onClick={() => void act(() => api.put('/api/platform/remote-access', { halted: !data.platformHalted }))}
            >
              {data.platformHalted ? 'Lift the platform-wide halt' : 'Halt remote access everywhere'}
            </Button>
            <span className="small muted">Platform staff. Halting ends every open session in every organisation within seconds.</span>
          </div>
        </Section>
      )}

      {playing !== null && (
        <RecordingPlayer
          sessionId={playing.id}
          title={`${playing.label} — shell by ${playing.openedByLabel}`}
          onClose={() => {
            setPlaying(null);
            void load();
          }}
        />
      )}

      {opening !== null && (
        <SessionDrawer
          host={opening}
          onClose={() => {
            setOpening(null);
            void load();
          }}
          onDesktop={(connection) => {
            // The drawer hands the live session to the full-window view and
            // closes WITHOUT ending it.
            setDesktop({ connection, label: opening.label });
            setOpening(null);
          }}
        />
      )}

      {desktop !== null && (
        <DesktopWindow
          connection={desktop.connection}
          label={desktop.label}
          onEnd={() => {
            const { connection } = desktop;
            void api
              .post(`/api/remote-sessions/${connection.sessionId}/end`)
              .catch((err: unknown) => setError(failure(err)))
              .finally(() => {
                connection.ws.close();
                if (document.fullscreenElement !== null) void document.exitFullscreen();
                setDesktop(null);
                void load();
              });
          }}
        />
      )}
    </>
  );
}

function SessionDrawer({
  host,
  onClose,
  onDesktop,
}: {
  host: RemoteHost;
  onClose: () => void;
  onDesktop: (connection: DesktopConnection) => void;
}): React.JSX.Element {
  const [kind, setKind] = useState<'diagnostic' | 'shell' | 'desktop'>(
    host.desktopEnabled && host.os === 'windows' ? 'desktop' : host.shellEnabled ? 'shell' : 'diagnostic',
  );
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  // 066: until when this login session can open without a code (null: it needs one).
  const [mfaUntil, setMfaUntil] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState<OpenedSession | null>(null);
  const [shell, setShell] = useState<ShellSocket | null>(null);
  const [status, setStatus] = useState('');
  const [lines, setLines] = useState<Array<{ from: 'host' | 'you' | 'relay'; text: string }>>([]);
  const [draft, setDraft] = useState('');
  const socket = useRef<WebSocket | null>(null);

  const say = (from: 'host' | 'you' | 'relay', text: string): void => setLines((l) => [...l, { from, text }]);

  useEffect(() => () => socket.current?.close(), []);

  useEffect(() => {
    api
      .get<{ until: string | null }>('/api/remote-sessions/mfa-window')
      .then((r) => setMfaUntil(r.until))
      .catch(() => setMfaUntil(null));
  }, []);
  const windowOpen = mfaUntil !== null && new Date(mfaUntil).getTime() > Date.now();
  const untilText = mfaUntil === null ? '' : new Date(mfaUntil).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const connectDiagnostic = (o: OpenedSession): void => {
    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
    // The ticket rides as a second subprotocol, never in the URL (see src/remote/policy.ts).
    const ws = new WebSocket(`${scheme}://${window.location.host}${o.path}`, [o.protocol, `ticket.${o.ticket}`]);
    socket.current = ws;
    ws.onopen = () => say('relay', 'Connected to the relay. Waiting for the host…');
    ws.onmessage = (e) => {
      const text = typeof e.data === 'string' ? e.data : '[binary]';
      try {
        const m = JSON.parse(text) as { type?: string; host?: string; os?: string; version?: string };
        if (m.type === 'hello') {
          say('relay', `The host answered: ${m.host ?? '?'} (${m.os ?? '?'}, agent ${m.version ?? '?'}).`);
          return;
        }
      } catch {
        // Not JSON: an echo.
      }
      say('host', text);
    };
    ws.onclose = (e) => say('relay', `Session closed${e.reason === '' ? '' : `: ${e.reason}`}.`);
  };

  const end = async (): Promise<void> => {
    if (opened !== null) {
      try {
        await api.post(`/api/remote-sessions/${opened.id}/end`);
      } catch (err) {
        setError(failure(err));
      }
    }
    onClose();
  };

  return (
    <Drawer
      open
      onClose={onClose}
      width={kind === 'shell' && opened !== null ? 'lg' : 'md'}
      title={`Connect to ${host.label}`}
      subtitle={
        kind === 'desktop'
          ? 'The screen, mouse and keyboard. The person at the PC sees a bar while you are connected.'
          : kind === 'shell'
            ? 'A root shell, recorded in full.'
            : 'A diagnostic session: proves the path, touches nothing.'
      }
    >
      <div className={opened !== null && kind !== 'diagnostic' ? undefined : 'form-narrow'}>
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {opened === null ? (
          <>
            <fieldset className="remote-kind">
              <legend className="small">Session</legend>
              <label>
                <input type="radio" name="remote-kind" checked={kind === 'diagnostic'} onChange={() => setKind('diagnostic')} />{' '}
                Diagnostic — says who it is and echoes
              </label>
              <label title={host.shellEnabled ? undefined : 'Turn on the shell switch for this host first'}>
                <input
                  type="radio"
                  name="remote-kind"
                  checked={kind === 'shell'}
                  disabled={!host.shellEnabled}
                  onChange={() => setKind('shell')}
                />{' '}
                Shell — {host.os === 'windows' ? 'SYSTEM' : 'root'}, recorded
                {!host.shellEnabled && <span className="muted"> (off for this host)</span>}
              </label>
              <label title={host.desktopEnabled ? undefined : 'Turn on the desktop switch for this host first'}>
                <input
                  type="radio"
                  name="remote-kind"
                  checked={kind === 'desktop'}
                  disabled={!host.desktopEnabled}
                  onChange={() => setKind('desktop')}
                />{' '}
                Desktop — screen, mouse and keyboard
                {!host.desktopEnabled && <span className="muted"> (off for this host)</span>}
              </label>
            </fieldset>
            <Field id="remote-reason" label="Why" hint="Kept with the session and in the audit log.">
              <textarea id="remote-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <Field
              id="remote-code"
              label="Authenticator code"
              hint={
                windowOpen
                  ? `Not needed: you entered one in the last hour, good until ${untilText}. A new code starts a new hour.`
                  : 'Asked once an hour for remote access, even though you are signed in.'
              }
            >
              <input
                id="remote-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.trim())}
              />
            </Field>
            <Button
              variant="primary"
              busy={busy}
              icon={<Plug size={14} />}
              disabled={reason.trim().length < 5 || (code === '' && !windowOpen)}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  const o = await api.post<OpenedSession & { mfaUntil?: string }>('/api/remote-sessions', {
                    assetId: host.assetId,
                    reason,
                    ...(code === '' ? {} : { code }),
                    kind,
                  });
                  if (o.mfaUntil !== undefined) setMfaUntil(o.mfaUntil);
                  setOpened(o);
                  if (kind === 'diagnostic') connectDiagnostic(o);
                  else if (kind === 'desktop') {
                    // Not kept in `socket`: the drawer's unmount closes that,
                    // and this session outlives the drawer.
                    onDesktop(await openDesktop(o));
                  } else {
                    const opened = openShellSocket(o);
                    socket.current = opened.ws;
                    setShell(opened);
                  }
                } catch (err) {
                  setError(failure(err));
                  // The hour may have run out while the dialog was open.
                  if (code === '') setMfaUntil(null);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Connect
            </Button>
          </>
        ) : kind === 'shell' ? (
          <>
            <div className="small muted remote-status" role="status" aria-live="polite">
              {status}
            </div>
            {shell !== null && <ShellTerminal socket={shell} onStatus={setStatus} />}
            <div className="row">
              <Button variant="danger" icon={<Square size={14} />} onClick={() => void end()}>
                End session
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="remote-log mono small" role="log" aria-live="polite">
              {lines.map((l, i) => (
                <div key={i} className={l.from === 'relay' ? 'muted' : undefined}>
                  {l.from === 'you' ? '> ' : l.from === 'host' ? '< ' : ''}
                  {l.text}
                </div>
              ))}
            </div>
            <form
              className="remote-send"
              onSubmit={(e) => {
                e.preventDefault();
                if (draft === '' || socket.current?.readyState !== WebSocket.OPEN) return;
                socket.current.send(draft);
                say('you', draft);
                setDraft('');
              }}
            >
              <input aria-label="Send to the host" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type, and the host echoes it" />
              <Button type="submit" size="sm">
                Send
              </Button>
            </form>
            <div className="row">
              <Button variant="danger" icon={<Square size={14} />} onClick={() => void end()}>
                End session
              </Button>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
